/**
 * Reiter *Termine* eines Objekts (`object-panel-termine`, AK-422) — dieselbe
 * Aufteilung wie `TaskList`/`features/tasks/TaskList.tsx`: Die Route
 * (`routes/ObjectDetail.tsx`) besitzt die Abfrage (`?scope=`), diese
 * Komponente bekommt nur das Ergebnis.
 *
 * Anders als `task-done-<id>` (sofort) öffnet `duedate-complete-<id>` ein
 * eigenes Sheet (`DueDateCompleteSheet`, Spec „Aktionen": „fragt nicht nach,
 * fragt nach dem Tag") — drei Sheets teilen sich deshalb **einen**
 * `sheetState` für Anlegen/Ändern und einen zweiten, unabhängigen Zustand
 * für „wird gerade erledigt", weil beide gleichzeitig unterschiedliche
 * Termine betreffen könnten (Zeile A ändern, während Zeile B im
 * Erledigen-Sheet steht, ist zwar am Bildschirm nie beides offen — aber
 * `DueDateSheet` und `DueDateCompleteSheet` sind unterschiedliche
 * Formulare, kein gemeinsamer Modus wie bei `task-sheet`).
 */

import { useState } from 'react'
import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { useDisableDueDate, useEnableDueDate } from '@/features/schedule/api'
import type { DueDatesPage, DueDateRow as DueDateRowType } from '@/features/schedule/api'
import { DueDateCompleteSheet } from '@/features/schedule/DueDateCompleteSheet'
import { DueDateRow } from '@/features/schedule/DueDateRow'
import { DueDateSheet } from '@/features/schedule/DueDateSheet'

type SheetState = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly dueDate: DueDateRowType }

interface DueDateListProps {
  readonly query: UseQueryResult<DueDatesPage> | UseInfiniteQueryResult<InfiniteData<DueDatesPage>>
  /** Das Objekt, an dem ein neuer Termin entstünde. */
  readonly subjectId: string
  readonly subjectLabel: string
  readonly canAdd: boolean
  readonly emptyTitle: string
  readonly emptyHint: string
}

export function DueDateList({ query, subjectId, subjectLabel, canAdd, emptyTitle, emptyHint }: DueDateListProps) {
  const disableMutation = useDisableDueDate()
  const enableMutation = useEnableDueDate()
  const [sheetState, setSheetState] = useState<SheetState | null>(null)
  const [completingDueDate, setCompletingDueDate] = useState<DueDateRowType | null>(null)
  // D1: ein 409 aus der Nebenläufigkeit (Spec „Nebenläufigkeit") ging bisher
  // in eine unbehandelte Rejection — derselbe Kanal wie `TenantLedger.tsx`
  // (`onError`/`ApiError`), Bezeichner wie `duedate-list-action-error` auf iOS
  // (`DueDateListView.swift`).
  const [actionError, setActionError] = useState<string | undefined>(undefined)
  const accessError = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  const hasReadableData = query.data !== undefined && !accessError
  const pageItems = !hasReadableData ? [] : 'pages' in query.data ? query.data.pages.flatMap((page) => page.items) : query.data.items
  const dueDates = [...new Map(pageItems.map((item) => [item.id, item])).values()]
  const pagedQuery = 'fetchNextPage' in query ? query : undefined

  async function disable(dueDate: DueDateRowType) {
    setActionError(undefined)
    try {
      await disableMutation.mutateAsync({ dueDateId: dueDate.id, ifMatch: String(dueDate.version) })
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function enable(dueDate: DueDateRowType) {
    setActionError(undefined)
    try {
      await enableMutation.mutateAsync({ dueDateId: dueDate.id, ifMatch: String(dueDate.version) })
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  const editingDueDate = sheetState?.mode === 'edit' ? sheetState.dueDate : null

  const addButton = canAdd && (
    <button
      type="button"
      data-testid="duedate-add"
      onClick={() => {
        setSheetState({ mode: 'create' })
      }}
      className="border-border text-text hover:bg-bg rounded-card text-body border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
    >
      + Termin
    </button>
  )

  return (
    <div data-testid="duedate-list">
      {query.isPending && <SkeletonRows testId="duedate-list-loading" rows={4} />}

      {(accessError || (query.isError && !hasReadableData)) && (
        <ErrorState
          testId="duedate-list-error"
          retryTestId="duedate-list-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {hasReadableData && dueDates.length === 0 && (
        <EmptyState testId="duedate-list-empty" title={emptyTitle} hint={emptyHint} action={addButton} />
      )}

      {hasReadableData && dueDates.length > 0 && (
        <>
          {actionError !== undefined && (
            <p
              data-testid="duedate-list-action-error"
              role="alert"
              className="text-label text-status-overdue px-(--page-x) py-2"
            >
              {actionError}
            </p>
          )}
          {canAdd && <div className="flex justify-end px-(--page-x) py-2">{addButton}</div>}
          <ul>
            {dueDates.map((dueDate) => (
              <DueDateRow
                key={dueDate.id}
                dueDate={dueDate}
                onOpen={() => {
                  setSheetState({ mode: 'edit', dueDate })
                }}
                onComplete={() => {
                  setCompletingDueDate(dueDate)
                }}
                onDisable={() => {
                  void disable(dueDate)
                }}
                onEnable={() => {
                  void enable(dueDate)
                }}
              />
            ))}
          </ul>
          {pagedQuery?.hasNextPage && (
            <button
              type="button"
              data-testid="object-facet-termine-more"
              disabled={pagedQuery.isFetchingNextPage}
              onClick={() => {
                void pagedQuery.fetchNextPage()
              }}
              className="border-border text-text m-2 self-start border px-3"
            >
              Mehr laden
            </button>
          )}
        </>
      )}

      <DueDateSheet
        open={sheetState !== null}
        onOpenChange={(open) => {
          if (!open) setSheetState(null)
        }}
        subjectId={subjectId}
        subjectLabel={subjectLabel}
        dueDate={editingDueDate}
      />

      <DueDateCompleteSheet
        dueDate={completingDueDate}
        onOpenChange={(open) => {
          if (!open) setCompletingDueDate(null)
        }}
      />
    </div>
  )
}
